import { useState, useEffect } from "react";
import api from "../services/api";

export const useSubscriptionLimits = () => {
  const [usage, setUsage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usageRes, subRes] = await Promise.all([
        api.get("/subscriptions/usage"),
        api.get("/subscriptions/current"),
      ]);

      if (usageRes.data.success) {
        setUsage(usageRes.data.usage);
      }
      if (subRes.data.success) {
        setSubscription(subRes.data.subscription);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching subscription data:", err);
      setError(
        err.response?.data?.message || "Failed to fetch subscription data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const checkLimit = (feature) => {
    if (!usage || !subscription)
      return { canProceed: true, isNearLimit: false };

    const plan = subscription.subscriptionPlan;
    let current, limit;

    switch (feature) {
      case "customers":
        current = usage.customers;
        limit = plan.maxCustomers;
        break;
      case "groups":
        current = usage.groups;
        limit = plan.maxGroups;
        break;
      case "messages":
        current = usage.messages;
        limit = plan.maxMessages;
        break;
      default:
        return { canProceed: true, isNearLimit: false };
    }

    if (limit === -1) {
      return { canProceed: true, isNearLimit: false, isUnlimited: true };
    }

    const percentage = (current / limit) * 100;
    const canProceed = current < limit;
    const isNearLimit = percentage >= 80;
    const isAtLimit = percentage >= 100;

    return {
      canProceed,
      isNearLimit,
      isAtLimit,
      current,
      limit,
      percentage,
      remaining: limit - current,
    };
  };

  const handleApiError = (error) => {
    if (error.response?.status === 403) {
      const errorData = error.response.data;
      if (errorData.code?.includes("LIMIT_EXCEEDED")) {
        return {
          isLimitError: true,
          feature: errorData.code.replace("_LIMIT_EXCEEDED", "").toLowerCase(),
          current: errorData.current,
          limit: errorData.limit,
          message: errorData.message,
        };
      }
    }
    return { isLimitError: false };
  };

  return {
    usage,
    subscription,
    loading,
    error,
    refetch: fetchData,
    checkLimit,
    handleApiError,
  };
};

export default useSubscriptionLimits;
